<template>
    <div class="product-map">
        <MapFilter v-if="showFilters" ref="filter" :categories="filters" :selected.sync="activeFilter" />
        <div ref="map" class="product-map__map"></div>
    </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import { uniq, get } from 'lodash';
import MapFilter from './ProductMapFilter.vue';

export default {
    components: {
        MapFilter
    },

    props: {
        // :id="{{ props.mapId }}"
        mapstyle: {
            type: String,
            required: true
        },

        lng: {
            type: Number,
            required: true
        },

        lat: {
            type: Number,
            required: true
        },
        
        maxzoom: {
            type: Number,
            required: true
        },

        minzoom: {
            type: Number,
            required: true
        },
        
        zoom: {
            type: Number,
            required: true
        },
        
        products: {
            type: Object,
            required: true
        },

        showFilters: {
            type: Boolean,
            default: true
        },

        filters: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            map: null,
            activeFilter: null,
            activeMarkerId: null,
            activePopupId: null
        }
    },

    computed: {
        // A list of all categories used across the products.
        // Used to create marker layers and set icons.
        allCategories() {
            return uniq(this.products.data.features.map((product) => product.properties.primaryCategory));
        },

        categoryLayers() {
            return this.allCategories.map((c) => `markers-${c}`);
        },

        markerWatcher() {
          return `${this.activeMarkerId}|${this.activePopupId}`;
        },
    },

    watch: {
        activeFilter(value) {
            this.filterByCategory(value);
        },

        markerWatcher() {
            const activeMarkerId = this.activeMarkerId || 'none';
            const activePopupId = this.activePopupId || 'none';

            const filters = activeMarkerId === activePopupId ? activeMarkerId : [activeMarkerId, activePopupId];

            this.allCategories.forEach((category) => {
                const icon = this.activeFilter?.slug || category;

                this.map.setLayoutProperty(
                    `markers-${category}`,
                    'icon-image',
                    [
                        'match',
                        ['get', 'id'],
                        filters,
                        `${icon}-active`,
                        `${icon}-inactive`
                    ]
                );
            });
        }
    },

    mounted() {
        mapboxgl.accessToken = 'pk.eyJ1IjoiZGVzdGluYXRpb25naXBwc2xhbmQiLCJhIjoiY2twbHZtMmpjMThmdTJvbnlpNjdjZGh2NCJ9.MA_glC5qr526qhpBLW-4kw';
                
        this.map = new mapboxgl.Map({
            container: this.$refs.map,
            style: `mapbox://styles/${this.mapstyle}`,
            center: [this.lng, this.lat],
            zoom: this.zoom,
            minZoom: this.minzoom,
            maxZoom: this.maxzoom,
            cooperativeGestures: true
        });

        // General map config
        const navControl = new mapboxgl.NavigationControl({ visualizePitch: true });
        this.map.addControl(navControl, 'bottom-right');

        this.map.on('load', () => {
            this.map.addSource('products', this.products);

            this.initMarkers();
            this.initPopups();

            const terrainRgb = this.map.getSource('mapbox://mapbox.terrain-rgb');
            terrainRgb.attribution = '';
        });
        
        this.map.on('click', () => {
            if (this.$refs.filter) this.$refs.filter.close();
        });
    },

    methods: {
        initMarkers() {
            // Load all icons
            const path = '/images/mapbox/pins';
            const categoryIcons = uniq([...this.filters.map((c) => c.slug), ...this.allCategories]);
            let icons = categoryIcons.map((category) => [
                {
                    category,
                    state: 'active'
                },{
                    category,
                    state: 'inactive'
                }
            ]);

            icons = icons.flat(1);

            Promise.all(
                icons.map(({ category, state }) => new Promise((resolve, reject) => {
                    const handle = `${category}-${state}`;

                    this.map.loadImage(`${path}/${handle}.png?v4`, (error, res) => {
                        if (error) {
                            this.map.loadImage(`${path}/fallback-${state}.png?v4`, (error, fallback) => {
                                this.map.addImage(handle, fallback);
                                resolve();
                            });

                            return;
                        }

                        this.map.addImage(handle, res);
                        resolve();
                    });
                }))
            ).then(() => {
                this.addMarkerLayers();
            });
        },
        
        addMarkerLayers() {
            this.allCategories.forEach((category) => {
                this.map.addLayer({
                    id: `markers-${category}`,
                    type: 'symbol',
                    source: 'products',
                    filter: ['==', 'primaryCategory', category],
                    layout: {
                        'icon-allow-overlap': true,
                        'icon-size': ['interpolate', ['linear'], ['zoom'], 2, 0.01, 15, 0.4],
                        'icon-anchor': 'center',
                        'icon-image': `${category}-inactive`
                    }
                });
            });

            this.map.on('mousemove', (e) => {
                const markers = this.map.queryRenderedFeatures(e.point, { layers: this.categoryLayers });
                this.activeMarkerId = get(markers, '[0].properties.id');
            });
        },

        initPopups() {
            const template = (product) =>
                `<div class="map-card__overlay">
                  <div class="map-card__image-wrapper">
                    <a class="w-full h-full" target="_blank" href="${product.properties.link}">
                      <img class="map-card__image" src="${product.properties.image}"/>
                    </a>
                  </div>
                  <div class="map-card__content">
                    <div class="map-card__body">
                      <a href="${product.properties.link}">
                        <h2 class="map-card__title h5">${product.properties.title}</h2>
                      </a>
                      <a href="${product.properties.link}">
                        <p class="map-card__description">${product.properties.description}</p>
                      </a>
                    </div>
                  </div>
                </div>
                <div class="js-map-card-toggle map-card__toggle--mobile">
                  <button type="button" class="map-card__toggle" />
                </div>`;

            let activePopup = null;

            this.map.on('click', (e) => {
                const markers = this.map.queryRenderedFeatures(e.point, { layers: this.categoryLayers });

                if (!markers.length) return;

                const product = markers[0];

                if (this.activePopupId) {
                    if (this.activePopupId !== product.properties.id) {
                        activePopup.remove();
                    } else {
                        return false;
                    }
                }

                activePopup = new mapboxgl.Popup({ className: 'map-card', offset: 20 })
                    .setLngLat(product.geometry.coordinates)
                    .setHTML(template(product))
                    .on('open', (e) => {
                        const container = e.target._container;
                        const toggle = container.querySelector('.js-map-card-toggle');

                        toggle.onclick = () => {
                            container.classList.add('is-expanded');
                        };
                    })
                    .addTo(this.map);

                this.activePopupId = product.properties.id;

                //map.easeTo: used to adjust popup position
                const padding = {};
                padding['top'] = 1;
                this.map.easeTo({
                    padding: padding,
                    duration: 500
                });

                function closePopup() {
                    setTimeout(() => {
                        if (document.querySelector('.map-card')) return;

                        activePopup = null;
                        this.activePopupId = null;
                    }, 250);
                }

                activePopup.once('close', closePopup.bind(this));
            });
        },

        filterByCategory(active) {
            const clone = JSON.parse(JSON.stringify(this.products.data));

            if (active) {
                const filteredSet = clone.features.filter((p) => p.properties.categories.includes(active.slug));
                clone.features = filteredSet;
            }

            this.map.getSource('products').setData(clone);

            // Update all layers with active filter icon
            this.allCategories.forEach((category) => {
                const icon = active?.slug || category;

                this.map.setLayoutProperty(
                    `markers-${category}`,
                    'icon-image',
                    `${icon}-inactive`
                );
            });
        }
    }
}
</script>

<style lang="scss">
.product-map {
  position: relative;
}

.product-map__map {
  width: 100%;
  height: 700px;
}

.product-map__map {
  .mapboxgl-canvas {
    &:focus {
      outline: none;
    }
  }
}

@include media-query(desk) {
  // Map fade gradient for desktop only
//   .mapboxgl-canvas-container:after {
//       background: linear-gradient(
//         180deg,
//         rgba(255,255,255,0) 0%,
//         rgba(255,255,255,1) 100%
//       );
//       content: "";
//       position: absolute;
//       bottom: 0;
//       right: 0;
//       left: 0;
//       z-index: 10;
//       pointer-events: none;
//       height: 25%;
//   }
  .mapboxgl-control-container {
    position: inherit;
    z-index: 10;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
}


/* Map Card */
.mapboxgl-popup.map-card {
  position: absolute;
  z-index: 50; // Add to z-index and place above navigation
  width: 100%;
  height: auto;
  opacity: 0;
  transition: opacity ease-in 0.5s;
  
  // TODO - Make DRY
  @include media-query(desk) {
    position: absolute;
    bottom: auto;
    width: 250px;
    height: auto;
    padding: 10px;
    border-bottom: 0;
    background-color: transparent;
    z-index: 10; // Keep below header, but above map fade
  }

  // TODO - Make DRY
  @include media-query(portable) {
    left: 0;
    bottom: 0;
    transform: none !important;
  }

  &.is-expanded {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: auto;
    background-color: rgba(000, 000, 000, 0.5);

    @include media-query(desk) {
      position: absolute;
      top: 0;
      left: 40px;
      bottom: auto;
      width: 380px;
      height: auto;
      padding: 10px;
      border-bottom: 0;
      background-color: transparent;
    }

    @media screen and (max-height: 630px) {
      align-items: flex-start;
    }
  }
}

.map-card {
  .mapboxgl-popup-content {
    width: 100vw;
    min-height: 75px;
    overflow: hidden;
    display: flex;
    align-items: center;
    flex-direction: row;
    // Use font declaration to revert baseline back to local site
    // This is due to mapbox setting it's own font at 12px
    font: 16px/1.25 Helvetica Neue,Arial,Helvetica,sans-serif;
    border-radius: 7px;
    box-shadow: rgba(0, 0, 0, 0.28) 0px 8px 28px 0px;
    @include media-query(desk) {
      display: block;
      width: 100%;
      height: auto;
    }

     @include media-query(portable) {      
      margin-left: 20px;    
      margin-right: 45px;
      margin-bottom: 10px;
      width: calc(100vw - 65px);
    }

    @include media-query(tiny) {    
      margin-left: 10px;     
      width: calc(100vw - 55px);
    }
  }

  &.is-expanded .mapboxgl-popup-content {
    display: block;
    width: 80vw;
    height: auto;
    margin: 0 auto;
    padding: 0 0 2em !important; // TODO - Refactor via Mapbox.vue
    overflow: scroll;

    @include media-query(desk) {
      width: 100%;
    }
  }

  .mapboxgl-popup-tip {
    display: none;
  }
}

.map-card__overlay {
  background: white;
  display: flex;
  align-items: center;
  overflow: hidden;
  flex: 10;

  @include media-query(desk) {
    bottom: 0;
    display: block;
    top: 30px;
  }
}

.map-card__content {
  display: flex;
  flex: 8;
  padding: 0 .7em;
  overflow: hidden;

  .map-card.is-expanded & {
    padding: 0 1.8em;

    &:after,
    &:before {
      content: "";
    }
  }


  @include media-query(desk) {
    width: 100%;
    &:after,
    &:before {
      content: "";
    }
  }

  .map-card.is-hidden & {
    opacity: 0;
  }
}

.map-card__body {
  @include media-query(desk) {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }
}

.map-card__image-wrapper {
  overflow: hidden;
  flex: 7; // Square up
  max-width: calc(150px - 8vw);
  aspect-ratio: 1 / 1; // Keep image square-ish
  background-color: #EDEDED;

  .map-card.is-expanded & {
    display: block;
    position: relative;
    width: 100%;
    height: auto;
    margin-bottom: 2em;
  }


  @include media-query(desk) {
    width: 100%;
    height: 130px;
    max-width: unset;
    margin-bottom: .7em;
    aspect-ratio: unset; // Unset ratio variables
  }
}

.map-card__image {
  opacity: 1;
  transition: opacity 0.2s ease;
  -o-object-fit: cover;
  object-fit: cover;
  max-width: unset;
  width: 100%;
  height: 100%;

  .map-card.is-hidden & {
    opacity: 0;
  }

  .map-card.is-expanded & {
    max-height: 40vh;
  }
}

.map-card__title {
  font-size: 1.1em;
  line-height: 1.875;

  @include media-query(desk) {
    margin-bottom: 10px;
    max-width: none;
  }

  @include media-query(smart) {
    font-size: 1em;
  }

  .map-card.is-expanded & {
    font-size: 1.75em;
  }


  .mapboxgl-popup.map-card.is-expanded & {
    max-width: none;
  }
}

.map-card__description {
  display: none; // Not shown by default (mobile relevant)
  overflow: hidden;
  margin-top: 15px;
  margin-bottom: 10px;
  font-size: 1em;
  line-height: 1.3;
  color: $base-font-color;
  font-family: $base-font-family;
  font-weight: 400;

  @include media-query(desk) {
    display: block;
  }

  .map-card.is-expanded & {
    display: block;
  }
}

.map-card__toggle--mobile {
  // flex: 1;
  line-height: 0;

  @include media-query(desk) {
    display: none;
  }

  .mapboxgl-popup.map-card.is-expanded & {
    display: none;
  }

  @include media-query(tiny) {
    display: none;
  }
}

.map-card__toggle {
  height: 28px;
  width: 28px;
  background-position: center;
  background-repeat: no-repeat;
  margin-top: 0;
  background-color: #0C5987;
  background-size: 20px;
  background-image: url(../assets/images/icon-expand.svg);

  .map-card.is-expanded & {
    display: none;
  }

  &:focus {
    outline: none;
  }
}

/* 
* Filter 
*/
.product-map__filter {
  font-size: 16px;
  width: 100%;
  position: absolute;
  z-index: 10;
  overflow: hidden;
  pointer-events: none;

  @include media-query(desk) {
    width: auto;
    min-width: 270px;
    right: 45px;
    top: 30px;
  }
}

.product-map__filter-drop-down {
  height: 80px;
  cursor: pointer;
  position: relative;
  z-index: 20;
  padding-left: 25px;
  padding-right: 25px;
  pointer-events: all;

  @include media-query(desk) {
    margin-right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    background-color: rgba(255, 255, 255, 0.08);
  }
}

.product-map__selected-filter {
  display: none;

  @include media-query(desk) {
    display: inline-block;
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 32px;
    padding-right: 32px;
    font-size: 16px;
  }
}

.product-map__filter-label {
  @include media-query(portable) {
    display: none;
  }
}

.product-map__filter-heading {
  display: block;
  padding-bottom: 20px;
  padding-top: 32px;
  padding-left: 25px;
  padding-right: 25px;

  @include media-query(desk) {
    display: none;
  }
}

.product-map__filter-toggle {
  @include media-query(portable) {
    width: 75px;
    height: 75px;
    border-radius: 50%;
    border: 2px solid getColor("white");
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: rgba(0, 0, 0, 0.28) 0px 8px 28px 0px;
    position: absolute;
    right: 24px;
    top: calc(75px / 2);
  }

  &.product-map__filter-toggle--desk {
    @include media-query(portable) {
      display: none;
    }
  }

  &.product-map__filter-toggle--portable {
    @include media-query(desk) {
      display: none;
    }
  }
}

.product-map__filter-icon {
  @include media-query(portable) {
    height: 16px;
    width: 22px;

    &.product-map__filter-icon--open {
      display: block;
      stroke: white;

      .is-open & {
        display: none;
      }
    }

    &.product-map__filter-icon--close {
      display: none;

      .is-open & {
        display: block;
      }
    }
  }

  @include media-query(desk) {
    .is-open & {
      transform: scaleY(-1);
    }
  }
}

/*
* TABS
*/
.product-map__tabs {
  font-size: 16px;
  overflow: hidden;
  transform: translateY(-20px);
  transition: all ease-in 0.2s;
  pointer-events: none;
  opacity: 0;

  @include media-query(portable) {
    border-top: 1px solid getColor("sand");
  }

  .is-open & {
    opacity: 1;
    pointer-events: all;
    transform: translateY(0);
  }
}

.product-map__tabs-list {
  border-top: 1px solid getColor("sand");
}

.product-map__tab {
  display: flex;
  padding-top: 15px;
  padding-bottom: 15px;
  padding-left: 25px;
  padding-right: 25px;
  min-height: 40px;
  line-height: 1;
  text-decoration: none;
  text-transform: none;
  vertical-align: middle;
  position: relative;
  cursor: pointer;
  white-space: nowrap;

  &:before {
    content: "";
    width: 20px;
    height: 20px;
    display: block;
    margin-right: 25px;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    background-image: url("/images/mapbox/filters/all.svg");
  }

  // Default tab
  &:hover,
  &.active {
    color: getColor("white");
    background-color: getColour("blue");

    &:before {
      background-image: url("/images/mapbox/filters/all-white.svg");
    }
  }
}

/* 
* Tab item icons
*/

.product-map__tab--destinations {
  &:before {
    background-image: url("/images/mapbox/filters/destinations.svg?v2");
  }

  &:hover,
  &.active {
    &:before {
      background-image: url("/images/mapbox/filters/destinations-white.svg?v2");
    }
  }
}

.product-map__tab--accommodation {
  &:before {
    background-image: url("/images/mapbox/filters/accommodation.svg?v2");
  }

  &:hover,
  &.active {
    &:before {
      background-image: url("/images/mapbox/filters/accommodation-white.svg?v2");
    }
  }
}

.product-map__tab--eat-drink {
  &:before {
    background-image: url("/images/mapbox/filters/eat-drink.svg?v2");
  }

  &:hover,
  &.active {
    &:before {
      background-image: url("/images/mapbox/filters/eat-drink-white.svg?v2");
    }
  }
}

.product-map__tab--do-see {
  &:before {
    background-image: url("/images/mapbox/filters/do-see.svg?v2");
  }

  &:hover,
  &.active {
    &:before {
      background-image: url("/images/mapbox/filters/do-see-white.svg?v2");
    }
  }
}

.product-map__tab--events {
  &:before {
    background-image: url("/images/mapbox/filters/events.svg?v2");
  }

  &:hover,
  &.active {
    &:before {
      background-image: url("/images/mapbox/filters/events-white.svg?v2");
    }
  }
}

.product-map__tab--travel-info {
  &:before {
    background-image: url("/images/mapbox/filters/travel-info.svg?v2");
  }

  &:hover,
  &.active {
    &:before {
      background-image: url("/images/mapbox/filters/travel-info-white.svg?v2");
    }
  }
}





.map-card.is-expanded {
  .map-card__overlay {
    display: block;
  }

  .map-card__image-wrapper {
    width: 100%;
    height: auto;
    aspect-ratio: unset; // Unset ratio variables
    max-width: unset;
  }

  .map-card__content {
    width: 100%;
  }
}

button.mapboxgl-popup-close-button {
  display: none;
}

/* 
* OVERRIDE MAPBOX STYLES 
*/
.map-card.mapboxgl-popup.mapboxgl-popup-anchor-top,
.map-card.mapboxgl-popup.mapboxgl-popup-anchor-bottom,
.map-card.mapboxgl-popup.mapboxgl-popup-anchor-right,
.map-card.mapboxgl-popup.mapboxgl-popup-anchor-left,
.map-card.mapboxgl-popup.mapboxgl-popup-anchor-top-left,
.map-card.mapboxgl-popup.mapboxgl-popup-anchor-top-right,
.map-card.mapboxgl-popup.mapboxgl-popup-anchor-bottom-left,
.map-card.mapboxgl-popup.mapboxgl-popup-anchor-bottom-right {
  opacity: 1;
  max-width: 100% !important;
  @include media-query(portable) {
    bottom: 0;
    top: auto;    
  }
}

.mapboxgl-ctrl.mapboxgl-ctrl-group {
  margin: 0 30px 30px 0;
}
</style>